import { useEffect } from "react";

// Consistent app-name prefix shared by every browser-tab title. Kept in
// sync with the in-app branding ("ConvoAI") and the static <title> in
// index.html. Renaming the app is out of scope, so this stays fixed.
export const APP_TITLE = "ConvoAI";

// Build a tab title from the app-name prefix plus an optional context
// string, e.g. `formatTitle("Jane Smith") -> "ConvoAI — Jane Smith"`.
// A null/empty context yields the bare app name.
export function formatTitle(context: string | null | undefined): string {
  const trimmed = context?.trim();
  return trimmed ? `${APP_TITLE} — ${trimmed}` : APP_TITLE;
}

// Set `document.title` from a context string for as long as the calling
// component is mounted.
//
// Pass:
//   - a string   → "ConvoAI — <context>"
//   - null       → just "ConvoAI"
//   - undefined  → do nothing (the view delegates its title elsewhere,
//                  e.g. a detail child that sets the title once its data
//                  loads). This lets a parent skip detail routes without
//                  clobbering the child's title.
export function useDocumentTitle(context: string | null | undefined): void {
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (context === undefined) return;
    document.title = formatTitle(context);
  }, [context]);
}
